<template>
    <div class="settings-container">
        <template v-if="userVoices.length">
            <label class="field-label" for="index-voice-select">Voice</label>
            <select id="index-voice-select" v-model="selectedVoiceId" class="field-control" @change="selectVoice">
                <option value="" disabled>Select a saved voice</option>
                <option v-for="voice in userVoices" :key="voice.voice_id || voice.voice_name" :value="voice.voice_id">
                    {{ voice.voice_name || voice.voice_id }}
                </option>
            </select>
        </template>
        <div v-else-if="!isLoadingVoices" class="empty-voices-state">
            <div class="empty-voices-title">No custom voices yet</div>
            <div class="empty-voices-text">Create a custom voice before using Pro.</div>
            <button class="create-voice-button" type="button" @click="goCreateCustomVoice">
                Create Custom Voice
            </button>
        </div>

        <label class="toggle-row">
            <input type="checkbox" v-model="localConfig.emo_random" @change="emitUpdate" />
            <span>Random emotion</span>
        </label>
    </div>
</template>

<script>
import { defineComponent, reactive, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { getUserVoicesByUserId } from '@/api/services';

export default defineComponent({
    name: 'SettingsIndexTTS2',
    props: {
        config: { type: Object, required: true }
    },
    emits: ['update:config'],
    setup(props, { emit }) {
        const router = useRouter();
        const userVoices = ref([]);
        const isLoadingVoices = ref(true);
        const selectedVoiceId = ref(props.config.voice_id || '');
        const localConfig = reactive({
            voice_name: props.config.voice_name || '',
            voice_id: props.config.voice_id || '',
            emo_random: props.config.emo_random ?? false,
        });

        function emitUpdate() {
            emit('update:config', { ...localConfig });
        }

        function selectVoice() {
            const selected = userVoices.value.find(v => String(v.voice_id) === String(selectedVoiceId.value));
            if (!selected) return;
            localConfig.voice_name = selected.voice_name || '';
            localConfig.voice_id = selected.voice_id || '';
            emitUpdate();
        }

        async function loadVoices() {
            isLoadingVoices.value = true;
            try {
                const voices = await getUserVoicesByUserId();
                userVoices.value = Array.isArray(voices) ? voices : [];
                const current = userVoices.value.find(v =>
                    String(v.voice_id) === String(localConfig.voice_id) ||
                    (localConfig.voice_name && v.voice_name === localConfig.voice_name)
                );
                const fallback = userVoices.value[0];
                const selected = current || fallback;
                if (selected && !localConfig.voice_id && !localConfig.voice_name) {
                    selectedVoiceId.value = selected.voice_id || '';
                    localConfig.voice_name = selected.voice_name || '';
                    localConfig.voice_id = selected.voice_id || '';
                    emitUpdate();
                } else if (selected) {
                    selectedVoiceId.value = selected.voice_id || '';
                }
            } catch (err) {
                console.error('Failed to load IndexTTS2 voices:', err);
            } finally {
                isLoadingVoices.value = false;
            }
        }

        function goCreateCustomVoice() {
            router.push({ name: 'choose-base-voice' });
        }

        watch(
            () => props.config,
            (newConfig) => {
                localConfig.voice_name = newConfig.voice_name || '';
                localConfig.voice_id = newConfig.voice_id || '';
                localConfig.emo_random = newConfig.emo_random ?? false;
                selectedVoiceId.value = localConfig.voice_id || '';
            },
            { deep: true }
        );

        loadVoices();

        return {
            userVoices,
            isLoadingVoices,
            selectedVoiceId,
            localConfig,
            selectVoice,
            emitUpdate,
            goCreateCustomVoice,
        };
    }
});
</script>

<style scoped>
.settings-container {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    height: 100%;
    min-height: 0;
    min-width: 260px;
    padding: 1rem;
}

.field-label {
    font-weight: 600;
    color: #333;
}

.field-control {
    width: 100%;
    border: 1px solid #ccc;
    border-radius: 6px;
    padding: 0.5rem 0.65rem;
    font: inherit;
    background: #fff;
}

.empty-voices-state {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.85rem;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    background: #fafafa;
}

.empty-voices-title {
    font-weight: 700;
    color: var(--text-primary);
}

.empty-voices-text {
    color: var(--text-secondary);
    font-size: 0.9rem;
    line-height: 1.35;
}

.create-voice-button {
    width: fit-content;
    border: none;
    border-radius: 6px;
    padding: 0.5rem 0.75rem;
    background: var(--purple);
    color: white;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s ease;
}

.create-voice-button:hover {
    background: var(--purple-hover);
}

.toggle-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 500;
    color: #333;
}
</style>
